import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { supabase } from '../lib/supabase';

type Mode = 'login' | 'register' | 'magic';

export function AuthPage() {
  const [mode, setMode]         = useState<Mode>('login');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [message, setMessage]   = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const { t } = useT();

  const msg = (type: 'ok' | 'err', text: string) => setMessage({ type, text });

  const handleEmail = async () => {
    if (!email) return msg('err', t('auth.email'));
    setLoading(true); setMessage(null);

    if (mode === 'magic') {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) msg('err', error.message);
      else msg('ok', t('auth.checkEmail'));
    } else if (mode === 'register') {
      if (password.length < 6) { setLoading(false); return msg('err', t('auth.weakPassword')); }
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) msg('err', error.message);
      else msg('ok', t('auth.confirmEmail'));
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) msg('err', t('auth.wrongCredentials'));
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google', options: { redirectTo: window.location.origin },
    });
    if (error) msg('err', error.message);
  };

  const modeLabel = (m: Mode) =>
    m === 'login' ? t('auth.tabLogin') : m === 'register' ? t('auth.tabRegister') : `🔗 ${t('auth.tabMagic')}`;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="brand-suit">◈</span>
          <span className="brand-name">GRIND<span className="brand-accent">LOG</span></span>
        </div>

        <div className="auth-tabs">
          {(['login', 'register', 'magic'] as Mode[]).map(m => (
            <button key={m} className={`auth-tab ${mode === m ? 'active' : ''}`}
              onClick={() => { setMode(m); setMessage(null); }}>
              {modeLabel(m)}
            </button>
          ))}
        </div>

        <div className="auth-form">
          <div className="form-field">
            <label className="field-label">{t('auth.email')}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleEmail()} placeholder="tu@email.com" autoFocus />
          </div>

          {mode !== 'magic' && (
            <div className="form-field">
              <label className="field-label">{t('auth.password')}</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmail()} placeholder="••••••••" />
            </div>
          )}

          {message && <div className={`auth-message ${message.type}`}>{message.text}</div>}

          <button className="btn btn-primary btn-save" onClick={handleEmail} disabled={loading} style={{ width: '100%' }}>
            {loading ? '...' :
              mode === 'login'    ? `${t('auth.login')} →` :
              mode === 'register' ? `${t('auth.register')} →` :
              `${t('auth.sendLink')} →`}
          </button>

          <div className="auth-divider"><span>o</span></div>

          <button className="btn-google" onClick={handleGoogle}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t('auth.googleLogin')}
          </button>
        </div>

        <p className="auth-footer-text">
          {mode === 'login' ? (
            <><button className="auth-link" onClick={() => setMode('register')}>{t('auth.register')}</button></>
          ) : (
            <><button className="auth-link" onClick={() => setMode('login')}>{t('auth.login')}</button></>
          )}
        </p>
      </div>
    </div>
  );
}
